import React from "react";
import { Car } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Section, SectionTitle } from "./Section";
import { formatMoney, fullResprayBase, fullResprayColorChange, FULL_SIZES } from "@/lib/pricing";

const SCOPE_OPTIONS = [["off", "Off"], ["exterior", "Exterior"], ["interior_exterior", <><span className="sm:hidden">Int + Ext</span><span className="hidden sm:inline">Interior + Exterior</span></>]];
const TIER_OPTIONS = [["standard", "Standard"], ["premium", "Premium"]];
const COLOUR_OPTIONS = [["same", "Same colour"], ["change", "Colour change"]];
const PAINT_OPTIONS = [["solid", "Solid / Metallic"], ["tricoat", "Tricoat / Pearl / Custom"]];

function Seg({ value, onChange, options, testid, color = "amber" }) { // options: [key, label]
  const on = color === "violet" ? "data-[on=true]:bg-violet-500 data-[on=true]:text-white" : "data-[on=true]:bg-amber-400 data-[on=true]:text-slate-950";
  return (
    <div className="flex rounded-lg border border-slate-700 bg-slate-950/60 p-1 gap-1" data-testid={testid}>
      {options.map(([k, en]) => (
        <button key={k} type="button" data-on={value === k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`} title={typeof en === "string" ? en : undefined}
          className={`flex-1 basis-auto px-2 h-9 rounded-md text-xs sm:text-sm font-semibold text-slate-400 text-center leading-tight whitespace-nowrap data-[on=false]:hover:text-slate-200 data-[on=false]:hover:bg-slate-800/70 active:scale-[0.98] transition-[background-color,color,transform] duration-150 ${on}`}>
          {en}
        </button>
      ))}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>{children}</div>
);

const PriceBox = ({ value, onChange, placeholder, testid, sym }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{sym}</span>
    <Input type="number" min="0" inputMode="decimal" value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="bg-slate-950/60 border-slate-700 h-9 pl-7 font-mono-num" />
  </div>
);

// Section 6 — whole-car respray priced from the Standard / Premium chart
export default function FullRespraySection({ settings, sym, fullRespray: fr, onChange }) {
  const on = fr.scope !== "off";
  const base = on ? fullResprayBase(settings, fr) : 0;
  const cc = on ? fullResprayColorChange(settings, fr) : 0;
  const downtime = settings.full_respray?.downtime?.[fr.scope];
  return (
    <Section>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <SectionTitle n="6" en="Full Car Respray" zh="全车喷漆" icon={Car} />
        <Seg testid="full-scope" value={fr.scope} onChange={(scope) => onChange({ scope })}
          options={SCOPE_OPTIONS} />
      </div>
      {!on && <p className="text-sm text-slate-400 mt-3">Whole-car respray from the Standard / Premium price chart. Turning it on clears the per-panel picks above.</p>}
      {on && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="full-respray-body">
          <Field label="Service tier 服务等级">
            <Seg testid="full-tier" color="violet" value={fr.tier} onChange={(tier) => onChange({ tier })}
              options={TIER_OPTIONS} />
          </Field>
          <Field label="Vehicle size 车型">
            <Seg testid="full-size" value={fr.size} onChange={(size) => onChange({ size })} options={FULL_SIZES} />
          </Field>
          <Field label="Colour 颜色">
            <Seg testid="full-color" value={fr.colorChange ? "change" : "same"} onChange={(v) => onChange({ colorChange: v === "change" })}
              options={COLOUR_OPTIONS} />
          </Field>
          <Field label="Paint type 漆种">
            <Seg testid="full-paint" color="violet" value={fr.paintType} onChange={(paintType) => onChange({ paintType })}
              options={PAINT_OPTIONS} />
          </Field>

          <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-slate-950/40 p-4 grid gap-4 sm:grid-cols-3">
            <Field label={fr.size === "xl" ? "Price · from chart (XL is a from-price)" : "Price · from chart"}>
              <PriceBox sym={sym} value={fr.priceOverride ?? base} placeholder={String(base)} testid="full-price-input" onChange={(v) => onChange({ priceOverride: v === "" ? null : v })} />
              <div className="text-[11px] text-slate-500">Chart: {formatMoney(sym, base)}{fr.size === "xl" ? " onwards" : ""}{fr.priceOverride !== null && fr.priceOverride !== undefined ? " · edited" : ""}</div>
            </Field>
            <Field label="Paint surcharge · subject to requirement">
              {fr.paintType === "tricoat"
                ? <PriceBox sym={sym} value={fr.paintSurcharge} placeholder="0" testid="full-paint-surcharge" onChange={(v) => onChange({ paintSurcharge: v })} />
                : <div className="h-9 flex items-center text-sm text-slate-400" data-testid="full-color-change-note">{cc > 0 ? `Colour change +${formatMoney(sym, cc)}` : fr.colorChange ? "Colour change included · free" : "Same colour · free"}</div>}
            </Field>
            <Field label="Conti / complexity surcharge">
              <PriceBox sym={sym} value={fr.complexitySurcharge} placeholder="0" testid="full-complexity-surcharge" onChange={(v) => onChange({ complexitySurcharge: v })} />
            </Field>
            {downtime && <div className="sm:col-span-3 text-xs text-slate-400" data-testid="full-downtime">Downtime 工期: <span className="text-slate-200 font-semibold">{downtime}</span>{fr.tier === "premium" ? " · premium-grade clearcoat · 90-day paint defects warranty" : " · mid-grade clearcoat · 90-day paint defects warranty"}</div>}
          </div>
        </div>
      )}
    </Section>
  );
}
